import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import Page from 'src/components/Page';
import axios from 'src/utils/axios';
import { havcNeutralButton, havcPrimaryButton } from '../../components/HavcComponents/havcTheme';
import CreateProjectDialog from '../../components/HavcComponents/CreateProjectDialog';
import UpdateProjectDialog from '../../components/HavcComponents/UpdateProjectDialog';
import Receipts from '../../components/HavcComponents/Receipts';
import DeleteProjectDialog from '../../components/HavcComponents/DeleteProjectDialog';
import HavcLogs from '../../components/HavcComponents/HavcLogs';
import CommentPopup from '../../components/HavcComponents/Comments';

// ==============================|| TASARIM TOKEN'LARI ||==============================
// Ekranin tum renkleri burada toplandi; degistirmek icin asagidaki iki nesne yeterli.

const C = {
  canvas: '#f1f5f9',
  surface: '#ffffff',
  headBg: '#f8fafc',
  rowAlt: '#fbfcfd',
  border: '#e2e8f0',
  borderSoft: '#eef2f7',
  ink: '#0f172a',
  inkStrong: '#1e293b',
  inkBody: '#334155',
  inkMuted: '#64748b',
  inkSoft: '#94a3b8'
};

const ACTION_TONES = {
  info: { fg: '#0284c7', bg: '#e0f2fe', hover: '#bae6fd' },
  warn: { fg: '#d97706', bg: '#fef3c7', hover: '#fde68a' },
  edit: { fg: '#2563eb', bg: '#dbeafe', hover: '#bfdbfe' },
  success: { fg: '#059669', bg: '#d1fae5', hover: '#a7f3d0' },
  danger: { fg: '#dc2626', bg: '#fee2e2', hover: '#fecaca' }
};

// sx nesneleri satir/render basina yeniden uretilmesin diye modul seviyesinde
// bir kez kuruluyor; 100 satirlik listede bu satir basina 10+ nesne demekti.
const ACTION_SX = Object.keys(ACTION_TONES).reduce((acc, tone) => {
  const palette = ACTION_TONES[tone];
  acc[tone] = {
    width: 36,
    height: 36,
    borderRadius: '10px',
    backgroundColor: palette.bg,
    color: palette.fg,
    transition: 'transform .15s ease, background-color .15s ease, box-shadow .15s ease',
    '&:hover': {
      backgroundColor: palette.hover,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 10px -4px ${alpha(palette.fg, 0.7)}`
    },
    '&.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#cbd5e1' },
    '& .MuiSvgIcon-root': { fontSize: '1.15rem' }
  };
  return acc;
}, {});

const buildCompletionStyles = ({ bar, track, text }) => ({
  label: { fontSize: '0.75rem', fontWeight: 700, color: text, mb: 0.5 },
  progress: {
    height: 6,
    borderRadius: 3,
    backgroundColor: track,
    '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: bar }
  }
});

const COMPLETION_STYLES = {
  high: buildCompletionStyles({ bar: '#10b981', track: '#d1fae5', text: '#065f46' }),
  mid: buildCompletionStyles({ bar: '#f59e0b', track: '#fef3c7', text: '#78350f' }),
  low: buildCompletionStyles({ bar: '#fb923c', track: '#fed7aa', text: '#9a3412' }),
  none: buildCompletionStyles({ bar: '#ef4444', track: '#fecaca', text: '#991b1b' })
};

// DB tarafindaki FNC_HAVC_PROJECT_COMPLETE_PERCENTAGE yuzdeyi '%75' seklinde,
// yuzde isareti BASTA donduruyor. parseInt('%75') NaN verir; bu yuzden once
// rakam disi karakterler temizleniyor.
const yuzdeSayisi = value => {
  const parsed = parseFloat(
    String(value ?? '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.')
  );
  return Number.isNaN(parsed) ? 0 : parsed;
};

const completionStyleOf = value => {
  const pct = yuzdeSayisi(value);
  if (pct >= 75) return COMPLETION_STYLES.high;
  if (pct >= 50) return COMPLETION_STYLES.mid;
  if (pct >= 25) return COMPLETION_STYLES.low;
  return COMPLETION_STYLES.none;
};

const styles = {
  root: theme => ({
    padding: theme.spacing(3),
    backgroundColor: C.canvas,
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
  }),
  hero: theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    flexWrap: 'wrap'
  }),
  heroIcon: theme => ({
    width: 52,
    height: 52,
    borderRadius: '16px',
    display: 'grid',
    placeItems: 'center',
    color: '#ffffff',
    flexShrink: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(
      theme.palette.primary.main,
      0.72
    )} 100%)`,
    boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.55)}`
  }),
  heroTitle: {
    color: C.ink,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.5px'
  },
  heroSubtitle: { color: C.inkMuted, marginTop: '2px' },
  countChip: theme => ({
    marginLeft: 'auto',
    fontWeight: 600,
    borderRadius: '10px',
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: '#9a5b00',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
  }),
  mainCard: {
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -12px rgba(16,24,40,0.18)',
    backgroundColor: C.surface,
    overflow: 'hidden'
  },
  filterBar: theme => ({
    padding: theme.spacing(2.5, 3),
    backgroundColor: C.headBg,
    borderBottom: `1px solid ${C.borderSoft}`,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
  }),
  field: theme => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: C.surface,
      transition: 'box-shadow .2s ease, border-color .2s ease',
      '& fieldset': { borderColor: C.border },
      '&:hover fieldset': { borderColor: alpha(theme.palette.primary.main, 0.6) },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`
      },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
    }
  }),
  // Sorgula: notr eylem, Yeni: ana eylem. Ikisi de ortak buton temasindan geliyor,
  // filtre satirinda esit genislikte dursunlar diye flex ekleniyor.
  filterNeutralButton: { ...havcNeutralButton, flex: 1 },
  filterPrimaryButton: { ...havcPrimaryButton, flex: 1 },
  tableWrap: {
    maxHeight: 'calc(100vh - 430px)',
    minHeight: 340,
    '&::-webkit-scrollbar': { width: 10, height: 10 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 8,
      border: '3px solid transparent',
      backgroundClip: 'content-box'
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94a3b8' }
  },
  headCell: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    padding: '12px 16px'
  },
  // Islemler sutunu saga yapisik: yatay kaydirmada bile hep gorunur.
  headCellActions: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    padding: '12px 16px',
    position: 'sticky',
    right: 0,
    zIndex: 4
  },
  // Arka plani satirdan miras aliyor; altindan kayan icerik gorunmesin.
  cellActions: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    backgroundColor: 'inherit',
    borderLeft: `1px solid ${C.border}`,
    boxShadow: '-6px 0 8px -8px rgba(15,23,42,0.35)'
  },
  bodyRow: theme => ({
    backgroundColor: C.surface,
    '& .MuiTableCell-body': {
      padding: '12px 16px',
      fontSize: '0.875rem',
      color: C.inkBody,
      borderBottom: `1px solid ${C.borderSoft}`,
      whiteSpace: 'nowrap'
    },
    '&:nth-of-type(even)': { backgroundColor: C.rowAlt },
    '&:hover': {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.06)} !important`
    },
    transition: 'background-color .16s ease'
  }),
  actionGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px',
    borderRadius: '13px',
    backgroundColor: C.headBg,
    border: `1px solid ${C.borderSoft}`
  },
  actionDivider: {
    width: '1px',
    alignSelf: 'stretch',
    marginInline: '2px',
    backgroundColor: C.border
  },
  emptyState: theme => ({
    padding: theme.spacing(9, 2),
    textAlign: 'center'
  }),
  sortLabel: theme => ({
    '&.Mui-active': { color: C.ink },
    '&.Mui-active .MuiTableSortLabel-icon': {
      color: `${theme.palette.primary.main} !important`
    },
    '&:hover': { color: C.inkStrong }
  }),
  cellCode: { fontWeight: 700, color: C.ink },
  cellOwner: { color: C.inkStrong, fontWeight: 500 },
  cellDesc: { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' },
  cellDate: { fontSize: '0.813rem', color: C.inkMuted },
  completionWrap: { minWidth: 112 },
  typeChip: {
    height: 24,
    borderRadius: '7px',
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: '#e0f2fe',
    color: '#0369a1'
  },
  skeletonCell: { padding: '12px 16px' },
  skeleton: { borderRadius: '6px' },
  footer: theme => ({
    padding: theme.spacing(1, 2),
    justifyContent: 'flex-end',
    backgroundColor: C.headBg,
    borderTop: `1px solid ${C.borderSoft}`
  })
};

// ==============================|| SIRALAMA ||==============================

// DB'den string olarak geliyor: "dd.MM.yyyy" ya da "dd.MM.yyyy HH:mm[:ss]".
// ISO gibi baska bir sekilde gelirse new Date()'e birakiliyor; cozulemezse 0.
const toSortableDate = value => {
  if (!value) return 0;

  const raw = String(value).trim();
  const tr = raw.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (tr) {
    const [, gun, ay, yil, saat = '0', dakika = '0', saniye = '0'] = tr;
    return new Date(+yil, +ay - 1, +gun, +saat, +dakika, +saniye).getTime();
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

// "%75" gibi degerler metin olarak siralanirsa 100 < 25 olur; sayiya cevriliyor.
const toSortableNumber = yuzdeSayisi;

const NUMERIC_COLUMNS = ['CompletionPercentage'];

function descendingComparator(a, b, orderBy) {
  let aValue;
  let bValue;

  if (orderBy === 'CreateDate') {
    aValue = toSortableDate(a[orderBy]);
    bValue = toSortableDate(b[orderBy]);
  } else if (NUMERIC_COLUMNS.indexOf(orderBy) !== -1) {
    aValue = toSortableNumber(a[orderBy]);
    bValue = toSortableNumber(b[orderBy]);
  } else {
    aValue = a[orderBy];
    bValue = b[orderBy];
  }

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

// orderBy bos iken API'den gelen sira korunur.
const getComparator = (order, orderBy) => {
  if (!orderBy) return () => 0;

  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

function stableSort(array, comparator) {
  const stabilizedThis = Array.isArray(array) ? array.map((el, index) => [el, index]) : [];
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells = [
  { id: 'ProjectCode', label: 'Proje Kodu', align: 'left' },
  { id: 'ProjectOwner', label: 'Proje Adı', align: 'left' },
  { id: 'Description', label: 'Proje', align: 'left' },
  { id: 'ProjectType', label: 'Proje Tipi', align: 'left' },
  { id: 'ServiceShop', label: 'Servis', align: 'left' },
  { id: 'MentorServiceShop', label: 'Mentor Servis', align: 'left' },
  { id: 'Dealer', label: 'Satıcı', align: 'left' },
  { id: 'Region', label: 'Bölge', align: 'left' },
  { id: 'City', label: 'İl', align: 'left' },
  { id: 'CompletionPercentage', label: 'Tamamlanma Oranı', align: 'left' },
  { id: 'CreateDate', label: 'Oluşturulma Tarihi', align: 'left' },
  { id: 'Actions', label: 'İşlemler', align: 'center', sortable: false }
];

// ==============================|| TABLO BASLIGI ||==============================

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = property => event => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={headCell.id === 'Actions' ? styles.headCellActions : styles.headCell}
          >
            {headCell.sortable === false ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={styles.sortLabel}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string
};

// ==============================|| SATIR AKSIYONU ||==============================

const RowAction = React.memo(function RowAction({
  title,
  tone,
  icon,
  disabled,
  onClick
}) {
  const button = (
    <IconButton
      size="small"
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      sx={ACTION_SX[tone]}
    >
      {icon}
    </IconButton>
  );

  return (
    <Tooltip title={title} arrow placement="top">
      {disabled ? <span>{button}</span> : button}
    </Tooltip>
  );
});

RowAction.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(Object.keys(ACTION_TONES)).isRequired,
  icon: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

// ==============================|| YUKLENIYOR ISKELETI ||==============================

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, rowIndex) => (
    <TableRow key={`skeleton-${rowIndex}`}>
      {headCells.map(headCell => (
        <TableCell key={headCell.id} sx={styles.skeletonCell}>
          <Skeleton variant="rounded" height={18} sx={styles.skeleton} />
        </TableCell>
      ))}
    </TableRow>
  ));

// ==============================|| HVAC ||==============================

function HVAC() {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedArea, setselectedArea] = React.useState([]);
  const [selectedAreaData, setselectedAreaData] = React.useState([]);
  const [selectedServiceShop, setselectedServiceShop] = React.useState([]);
  const [selectedServiceShopData, setselectedServiceShopData] = React.useState([]);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [HvacData, setHvacData] = React.useState([]);
  const [OpenCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [OpenUpdateDialog, setOpenUpdateDialog] = React.useState(false);
  const [OpenReceiptsDialog, setOpenReceiptsDialog] = React.useState(false);
  const [OpenDeleteProjectDialog, setOpenDeleteProjectDialog] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState([]);
  const [search, setSearch] = useState('');
  const [HavcAddressData, setHavcAddressData] = React.useState([]);
  const [permissions, setPermissions] = useState([]);
  const [exceptionalUsers, setExceptionalUsers] = useState([]);
  const [fislerYeniEkranAc, setFislerYeniEkranAc] = useState(false);
  const [OpenLogsDialog, setOpenLogsDialog] = React.useState(false);
  const [openCommentDialog, setOpenCommentDialog] = React.useState(false);
  const [uyari, setUyari] = useState('');

  const handleUpdateSuccess = projectId => {
    sorgula(projectId);
    setSearch(projectId);
  };

  useEffect(() => {
    try {
      const mainArray = JSON.parse(
        localStorage.getItem('HavcComboData').replace(/\\"/g, '"')
      );
      setselectedAreaData(mainArray[0].BOLGE);
      setselectedServiceShopData(mainArray[0].SERVIS);
      setPermissions(mainArray[0].ROLES);
      setExceptionalUsers(mainArray[0]?.EXUSERS);

      axios
        .get('/GetHavcComboAddressValues')
        .then(response => {
          setHavcAddressData(response.data.length === 0 ? [] : response.data);
        })
        .catch(() => {
          setUyari(
            'İl / ilçe / mahalle listeleri yüklenemedi. Yeni proje oluştururken adres alanları boş gelebilir; sayfayı yenileyip tekrar deneyin.'
          );
        });
    } catch {
      setUyari(
        'Kullanıcı ve yetki bilgileri yüklenemedi. Bazı alanlar ve butonlar kapalı görünebilir; sayfayı yenileyip tekrar deneyin.'
      );
    }
  }, []);

  // localStorage'daki kullanici bir kez cozuluyor: checkRolesAuth render basina
  // 4 kez cagriliyor ve eskiden her cagrida JSON.parse yapiliyordu.
  const oturumKullanicisi = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  }, []);

  // aramaMetni verilmezse ekrandaki arama kutusu kullanilir.
  //
  // DIKKAT: bu fonksiyon bir onClick'e DOGRUDAN baglanmamali. Oyle baglanirsa
  // React tiklama event'ini argüman olarak gecer; <button> elementinin bos
  // `value` ozelligi de arama metnini sessizce eziyordu ve SP bos arama alip
  // butun aktif projeleri donduruyordu. Cagiranlar `() => sorgula()` kullanir.
  const sorgula = aramaMetni => {
    setLoading(true);
    setHasSearched(true);
    setPage(0);

    const user = oturumKullanicisi;
    const bolge = selectedArea?.Id ? selectedArea?.Id : 0;
    const servis = selectedServiceShop?.Id ? selectedServiceShop?.Id : 0;
    const userId = user?.UserId?.toString();
    const roleId = user?.Role_Id;

    const query =
      typeof aramaMetni === 'string' || typeof aramaMetni === 'number'
        ? String(aramaMetni)
        : search;

    axios
      .get(
        `GetHavcProjects/${bolge}/${servis}/${roleId}/${userId}/${encodeURIComponent(
          query
        )}`
      )
      .then(response => {
        setHvacData(response.data);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  // Istisna kullanici listesindeyse rol 15'e yukseltiliyor (eski davranis birebir).
  const yetkiRolId = useMemo(() => {
    const userId = oturumKullanicisi?.UserId?.toString();
    const istisna = (exceptionalUsers || []).some(x => x.Id === userId);
    return istisna ? 15 : oturumKullanicisi?.Role_Id;
  }, [oturumKullanicisi, exceptionalUsers]);

  const checkRolesAuth = useCallback(
    field =>
      (permissions || []).some(
        x =>
          x.HavcPage === 'HVAC/index' &&
          x.ApolloRoleId === yetkiRolId &&
          x.HavcField === field
      ),
    [permissions, yetkiRolId]
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event?.target.value, 10));
    setPage(0);
  };

  const sortedRows = useMemo(
    () => stableSort(HvacData, getComparator(order, orderBy)),
    [HvacData, order, orderBy]
  );

  const visibleRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const showEmptyState = !loading && sortedRows.length === 0;

  return (
    <Page sx={styles.root} title={'Enerji Çözümleri'}>
      <Box sx={styles.hero}>
        <Box sx={styles.heroIcon}>
          <AcUnitIcon />
        </Box>
        <Box>
          <Typography variant="h4" sx={styles.heroTitle}>
            HVAC ve Enerji Çözümleri
          </Typography>
          <Typography variant="body2" sx={styles.heroSubtitle}>
            Proje yönetimi ve takip sistemi
          </Typography>
        </Box>
        {sortedRows.length > 0 && (
          <Chip size="small" label={`${sortedRows.length} proje`} sx={styles.countChip} />
        )}
      </Box>

      <Card sx={styles.mainCard} elevation={0}>
        <Box sx={styles.filterBar}>
          {uyari && (
            <Alert
              severity="warning"
              onClose={() => setUyari('')}
              sx={{ mb: 2, borderRadius: '10px' }}
            >
              {uyari}
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                id="hvac-filter-region"
                fullWidth
                disabled={!checkRolesAuth('regionAutocomplete')}
                value={selectedArea}
                options={selectedAreaData}
                getOptionLabel={option => (option.Dsc ? option.Dsc : '')}
                onChange={(event, newValue) => {
                  setselectedArea(newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Bölge"
                    variant="outlined"
                    placeholder="Bölge seçiniz"
                    sx={styles.field}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                id="hvac-filter-service"
                disabled={!checkRolesAuth('serviceAutocomplete')}
                fullWidth
                value={selectedServiceShop}
                options={selectedServiceShopData}
                getOptionLabel={option => (option.Dsc ? option.Dsc : '')}
                onChange={(event, newValue) => {
                  setselectedServiceShop(newValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Servis"
                    variant="outlined"
                    placeholder="Servis seçiniz"
                    sx={styles.field}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <TextField
                fullWidth
                id="hvac-filter-search"
                label="Arama"
                placeholder="Proje kodu, isim veya açıklama ile arayın..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    sorgula();
                  }
                }}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: C.inkSoft, fontSize: '1.15rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <Tooltip title="Temizle" arrow>
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
                variant="outlined"
                size="small"
                sx={styles.field}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => sorgula()}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={styles.filterNeutralButton}
                >
                  {loading ? 'Yükleniyor' : 'Sorgula'}
                </Button>
                <Tooltip
                  title={
                    checkRolesAuth('createProject')
                      ? 'Yeni proje oluştur'
                      : 'Bu işlem için yetkiniz bulunmuyor'
                  }
                  arrow
                >
                  <span style={{ display: 'flex', flex: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setOpenCreateDialog(true)}
                      startIcon={<AddIcon />}
                      disabled={!checkRolesAuth('createProject')}
                      sx={styles.filterPrimaryButton}
                    >
                      Yeni
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer sx={styles.tableWrap}>
            <Table stickyHeader aria-label="HVAC projeleri">
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {loading && <SkeletonRows />}

                {showEmptyState && (
                  <TableRow>
                    <TableCell colSpan={headCells.length} sx={{ borderBottom: 'none' }}>
                      <Box sx={styles.emptyState}>
                        {hasSearched ? (
                          <>
                            <SearchOffIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
                            <Typography variant="h6" sx={{ color: C.inkMuted, mb: 0.5 }}>
                              Sonuç Bulunamadı
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.inkSoft }}>
                              Arama kriterlerinize uygun proje bulunamadı. Lütfen farklı
                              filtreler deneyin.
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TravelExploreIcon
                              sx={{
                                fontSize: 56,
                                color: alpha(theme.palette.primary.main, 0.45),
                                mb: 1.5
                              }}
                            />
                            <Typography variant="h6" sx={{ color: C.inkMuted, mb: 0.5 }}>
                              Sorgulamaya Hazır
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.inkSoft }}>
                              Bölge ve servis seçip <strong>Sorgula</strong> düğmesine basın
                              ya da doğrudan proje kodu ile arama yapın.
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  visibleRows.map((row, index) => {
                    if (typeof row === 'number') return null;
                    const completion = completionStyleOf(row.CompletionPercentage);
                    const pct = yuzdeSayisi(row.CompletionPercentage);

                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.ProjectID ?? `hvac-row-${index}`}
                        sx={styles.bodyRow}
                      >
                        <TableCell sx={styles.cellCode}>{row.ProjectCode}</TableCell>
                        <TableCell sx={styles.cellOwner}>{row.ProjectOwner}</TableCell>
                        <TableCell sx={styles.cellDesc}>
                          <Tooltip title={row.Description || ''} arrow>
                            <span>{row.Description}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.ProjectType}
                            sx={styles.typeChip}
                          />
                        </TableCell>
                        <TableCell>{row.ServiceShop}</TableCell>
                        <TableCell>{row.MentorServiceShop}</TableCell>
                        <TableCell>{row.Dealer}</TableCell>
                        <TableCell>{row.Region}</TableCell>
                        <TableCell>{row.City}</TableCell>
                        <TableCell>
                          <Box sx={styles.completionWrap}>
                            <Typography component="div" sx={completion.label}>
                              {row.CompletionPercentage || '0%'}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Math.max(pct, 0), 100)}
                              sx={completion.progress}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={styles.cellDate}>{row.CreateDate}</TableCell>
                        <TableCell align="center" sx={styles.cellActions}>
                          <Box sx={styles.actionGroup}>
                            <RowAction
                              title="Yorumlar"
                              tone="info"
                              icon={<CommentIcon />}
                              onClick={() => {
                                setSelectedRow(row);
                                setOpenCommentDialog(true);
                              }}
                            />
                            <RowAction
                              title="Son Güncellemeler"
                              tone="warn"
                              icon={<NotificationsActiveIcon />}
                              disabled={!checkRolesAuth('Log')}
                              onClick={() => {
                                setSelectedRow(row);
                                setOpenLogsDialog(true);
                              }}
                            />

                            <Box sx={styles.actionDivider} />

                            <RowAction
                              title="Projeyi Düzenle"
                              tone="edit"
                              icon={<EditIcon />}
                              onClick={() => {
                                setSelectedRow(row);
                                setOpenUpdateDialog(true);
                              }}
                            />
                            <RowAction
                              title="Fiş İşlemleri"
                              tone="success"
                              icon={<ReceiptIcon />}
                              onClick={() => {
                                setSelectedRow(row);
                                setFislerYeniEkranAc(true);
                                setOpenReceiptsDialog(true);
                              }}
                            />

                            <Box sx={styles.actionDivider} />

                            <RowAction
                              title="Projeyi Sil"
                              tone="danger"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                setSelectedRow(row);
                                setOpenDeleteProjectDialog(true);
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>

        <Divider />

        <CardActions sx={styles.footer}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              '& .MuiTablePagination-toolbar': { minHeight: 48 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.8125rem',
                color: C.inkMuted,
                marginBottom: 0
              }
            }}
          />
        </CardActions>
      </Card>

      <CreateProjectDialog
        open={OpenCreateDialog}
        setOpen={setOpenCreateDialog}
        HavcAddressData={HavcAddressData}
        sorgula={sorgula}
      />
      <HavcLogs
        open={OpenLogsDialog}
        setOpen={setOpenLogsDialog}
        projectId={selectedRow.ProjectID}
        HavcAddressData={HavcAddressData}
      />
      <CommentPopup
        open={openCommentDialog}
        setOpen={setOpenCommentDialog}
        projectId={selectedRow.ProjectID}
      />
      <UpdateProjectDialog
        open={OpenUpdateDialog}
        setOpen={setOpenUpdateDialog}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        HavcAddressData={HavcAddressData}
        sorgula={sorgula}
        onUpdateSuccess={handleUpdateSuccess}
      />
      <Receipts
        open={OpenReceiptsDialog}
        setOpen={setOpenReceiptsDialog}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        sorgula={sorgula}
        fislerYeniEkraniAc={fislerYeniEkranAc}
        onUpdateSuccess={handleUpdateSuccess}
      />
      <DeleteProjectDialog
        open={OpenDeleteProjectDialog}
        setOpen={setOpenDeleteProjectDialog}
        ProjectID={selectedRow.ProjectID}
        sorgula={sorgula}
      />
    </Page>
  );
}

export default HVAC;
